import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Mail, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { downloadResume, mailto, resumeOptions } from "@/lib/peopleFinder";
import { cn } from "@/lib/utils";

const fmt = (d) => (d ? new Date(d).toLocaleDateString([], { dateStyle: "medium" }) : "");

// draft = {to, subject, body, job_id}; onSent fires after the mail draft opens.
export const ResumeEmailDialog = ({ draft, onClose, onSent }) => {
  const [opts, setOpts] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState("");

  useEffect(() => {
    if (!draft) return;
    setOpts(null); setPick(null); setFile("");
    resumeOptions(draft.job_id).then((o) => { setOpts(o); setPick(o[0] ? `${o[0].source}:${o[0].id}` : null); }).catch((e) => { toast.error(errMsg(e)); setOpts([]); });
  }, [draft]);
  if (!draft) return null;

  const go = async () => {
    const opt = opts.find((o) => `${o.source}:${o.id}` === pick);
    setBusy(true);
    try {
      const name = await downloadResume(opt);
      setFile(name);
      window.location.href = mailto(draft.to, draft.subject, draft.body);
      onSent?.();
    } catch (e) { toast.error(e.response?.status === 404 ? "That resume file isn't available. Pick another one." : errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-[#27272A] bg-[#121214] sm:max-w-md" data-testid="resume-email-dialog">
        <DialogHeader><DialogTitle className="font-display">Email with your resume</DialogTitle></DialogHeader>
        {file ? (
          <div className="space-y-3 text-sm" data-testid="resume-email-done">
            <p className="flex items-start gap-2 text-zinc-200"><Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-[#D4AF37]" />Your email draft is open. Attach <b className="break-all text-[#F3C649]">{file}</b> from your Downloads folder, then press send.</p>
            <div className="flex justify-end gap-2">
              <a href={mailto(draft.to, draft.subject, draft.body)} className="inline-flex h-9 items-center gap-1 rounded-md border border-[#27272A] px-3 text-xs text-zinc-200" data-testid="resume-email-reopen"><Mail className="h-3.5 w-3.5" />Open email again</a>
              <Button size="sm" onClick={onClose} data-testid="resume-email-close">Done</Button>
            </div>
          </div>
        ) : !opts ? <Loader2 className="mx-auto my-8 h-5 w-5 animate-spin text-[#D4AF37]" /> : !opts.length ? (
          <p className="text-sm text-zinc-400" data-testid="resume-email-none">No resume found yet. Upload one in your Profile or build one in Resume Studio first.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">Pick the resume to send{draft.to ? ` to ${draft.to}` : ""}. It downloads, then your email app opens with the message ready.</p>
            <div className="space-y-1.5">{opts.map((o) => {
              const k = `${o.source}:${o.id}`;
              return (
                <button key={k} onClick={() => setPick(k)} className={cn("flex w-full items-center gap-3 overflow-hidden rounded-lg border p-3 text-left", pick === k ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#27272A] hover:border-zinc-600")} data-testid={`resume-option-${o.source}-${o.id}`}>
                  <FileText className="h-4 w-4 shrink-0 text-[#D4AF37]" />
                  <span className="min-w-0 flex-1"><span className="flex min-w-0 items-center gap-2"><span className="truncate text-sm text-zinc-100">{o.label}</span>{o.source === "tailored" && <span className="shrink-0 rounded-full bg-emerald-500/15 px-1.5 text-[10px] text-emerald-300">Tailored to this job</span>}</span><span className="block truncate text-[11px] text-zinc-500">{[o.sub, fmt(o.at)].filter(Boolean).join(" · ")}</span></span>
                </button>
              );
            })}</div>
            <div className="flex justify-end"><Button onClick={go} disabled={!pick || busy} data-testid="resume-email-go">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}Download & open email</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
